import React, { useMemo, useState, useEffect } from 'react';
import { Form as RBForm, Spinner, Alert } from 'react-bootstrap';
import * as Yup from 'yup';
import styled from 'styled-components';
import { serviceExists, registerServiceProvider, addServiceToPanel } from 'services/serviceProviderSrv';

// === All styled components unchanged (same as before) ===
const PRIMARY = '#2563eb';
const PRIMARY_HOVER = '#1d4ed8';
const PRIMARY_FOCUS = 'rgba(37, 99, 235, 0.15)';
const BORDER = '#e2e8f0';
const TEXT_DARK = '#0f172a';
const TEXT_MUTED = '#64748b';
const BG_MUTED = '#f8fafc';

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const HelperText = styled.small`
  color: ${TEXT_MUTED};
`;

const SearchInput = styled.input`
  flex: 1 1 280px;
  min-width: 240px;
  height: 38px;
  padding: 8px 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  outline: none;
  transition: all 0.15s ease;
  &::placeholder { color: #94a3b8; }
  &:focus { border-color: ${PRIMARY}; box-shadow: 0 0 0 3px ${PRIMARY_FOCUS}; }
  &:disabled { background: ${BG_MUTED}; cursor: not-allowed; }
`;

const Input = styled.input`
  height: 38px;
  padding: 8px 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  outline: none;
  width: 100%;
  transition: all 0.15s ease;
  &::placeholder { color: #94a3b8; }
  &:focus { border-color: ${PRIMARY}; box-shadow: 0 0 0 3px ${PRIMARY_FOCUS}; }
  &[readOnly] { background: ${BG_MUTED}; }
`;

const Select = styled.select`
  height: 38px;
  padding: 8px 32px 8px 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  outline: none;
  width: 100%;
  background: white url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e") no-repeat right 12px center;
  background-size: 12px;
  appearance: none;
  &:focus { border-color: ${PRIMARY}; box-shadow: 0 0 0 3px ${PRIMARY_FOCUS}; }
`;

const SectionTitle = styled.div`
  font-weight: 700;
  color: ${TEXT_DARK};
  font-size: 1rem;
  margin: 1.5rem 0 0.75rem;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  outline: none;
  background: ${PRIMARY};
  color: #ffffff;
  &:hover { background: ${PRIMARY_HOVER}; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25); }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: #ffffff;
  color: ${TEXT_MUTED};
  border: 1px solid ${BORDER};
  &:hover { background: ${BG_MUTED}; border-color: #cbd5e1; }
`;

const CATEGORIES = ['GARAGE', 'MOTOR_VALUER'];

const newProviderSchema = Yup.object({
  name: Yup.string().trim().required('Provider name is required'),
  category: Yup.string().required('Category is required'),
  contactName: Yup.string().trim().required("Contact person's name is required"),
  contactEmail: Yup.string().email('Invalid email').required('Contact email is required'),
});

export default function AddServiceProvider({ onHide, onCreate }) {
  const [searchEmail, setSearchEmail] = useState('');
  const [foundProvider, setFoundProvider] = useState(null);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formValues, setFormValues] = useState({
    name: '',
    category: '',
    contactName: '',
    contactEmail: '',
  });

  const [validationErrors, setValidationErrors] = useState({});

  const trimmedEmail = searchEmail.trim().toLowerCase();
  const isNew = searched && !foundProvider;

  // Debounced form validation
  useEffect(() => {
    if (!isNew) {
      setValidationErrors({});
      return;
    }

    const timeout = setTimeout(() => {
      newProviderSchema.validate(formValues, { abortEarly: false })
        .then(() => setValidationErrors({}))
        .catch((err) => {
          const errors = {};
          err.inner.forEach((e) => errors[e.path] = e.message);
          setValidationErrors(errors);
        });
    }, 300);

    return () => clearTimeout(timeout);
  }, [formValues, isNew]);

  const resetForm = () => {
    setSearchEmail('');
    setFoundProvider(null);
    setSearched(false);
    setError('');
    setSuccessMessage('');
    setFormValues({ name: '', category: '', contactName: '', contactEmail: '' });
    setValidationErrors({});
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMessage('');
    setFoundProvider(null);

    if (!trimmedEmail) {
      setError('Please enter an email address.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSearching(true);

    try {
      const res = await serviceExists(trimmedEmail);
      const provider = res.data;
      console.log("provider===", provider)

      if (provider) {
        setFoundProvider(provider);
        setSuccessMessage(`Provider found: ${provider.serviceProviderPrimaryEmail}`);
      } else {
        setSuccessMessage('No provider found. You can register a new one below.');
      }
      setSearched(true);
    } catch (err) {
      setError('Failed to search provider. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (foundProvider) {
        // Existing provider → just add to panel
        await addServiceToPanel(foundProvider.serviceProviderSystemRef);
        setSuccessMessage('Provider successfully added to your panel!');
      } else {
        // New provider → validate + register + add to panel
        await newProviderSchema.validate(formValues, { abortEarly: false });

        const registered = await registerServiceProvider({
          email: trimmedEmail,
          name: formValues.name.trim(),
          category: formValues.category,
          contactPerson: {
            name: formValues.contactName.trim(),
            email: formValues.contactEmail.trim(),
          },
        });

        const newRef = registered?.data?.serviceProviderSystemRef;
        if (newRef) {
          await addServiceToPanel(newRef);
        }

        setSuccessMessage('New provider registered and added to your panel!');
      }

      // Auto-close after success
      setTimeout(() => {
        onCreate?.();
        onHide?.();
      }, 1200);

    } catch (err) {
      if (err.name === 'ValidationError') {
        const errors = {};
        err.inner.forEach((e) => errors[e.path] = e.message);
        setValidationErrors(errors);
      } else {
        setError(err.response?.data?.message || 'Operation failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}

      <RBForm.Group className="mb-4">
        <label className="form-label fw-semibold">Provider Email</label>
        <Row>
          <SearchInput
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="provider@example.com"
            disabled={searching}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && !searching && handleSearch(e)}
          />
          <PrimaryButton onClick={handleSearch} disabled={searching || !trimmedEmail}>
            {searching ? (
              <>
                <Spinner animation="border" size="sm" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </PrimaryButton>
        </Row>
        <HelperText>Enter the provider's email to check if they already exist.</HelperText>
      </RBForm.Group>

      {searched && (
        <form onSubmit={handleSubmit}>
          {foundProvider && (
            <Alert variant="light" className="mb-4">
              <strong>{foundProvider.name || foundProvider.email}</strong><br />
              <small>This provider already exists in the system.</small>
            </Alert>
          )}

          {isNew && (
            <>
              <RBForm.Group className="mb-3">
                <label className="form-label">Email (will be registered)</label>
                <Input value={searchEmail} readOnly />
              </RBForm.Group>

              <RBForm.Group className="mb-3">
                <label className="form-label">Provider Name *</label>
                <Input
                  value={formValues.name}
                  onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                  placeholder="e.g. City Garage Ltd"
                />
                {validationErrors.name && <div className="text-danger small mt-1">{validationErrors.name}</div>}
              </RBForm.Group>

              <RBForm.Group className="mb-3">
                <label className="form-label">Category *</label>
                <Select
                  value={formValues.category}
                  onChange={(e) => setFormValues({ ...formValues, category: e.target.value })}
                >
                  <option value="" disabled>Select category</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                  ))}
                </Select>
                {validationErrors.category && <div className="text-danger small mt-1">{validationErrors.category}</div>}
              </RBForm.Group>

              <hr className="my-4" />

              <SectionTitle>Contact Person</SectionTitle>

              <RBForm.Group className="mb-3">
                <label className="form-label">Name *</label>
                <Input
                  value={formValues.contactName}
                  onChange={(e) => setFormValues({ ...formValues, contactName: e.target.value })}
                  placeholder="John Doe"
                />
                {validationErrors.contactName && <div className="text-danger small mt-1">{validationErrors.contactName}</div>}
              </RBForm.Group>

              <RBForm.Group className="mb-3">
                <label className="form-label">Email *</label>
                <Input
                  type="email"
                  value={formValues.contactEmail}
                  onChange={(e) => setFormValues({ ...formValues, contactEmail: e.target.value })}
                  placeholder="john@provider.com"
                />
                {validationErrors.contactEmail && <div className="text-danger small mt-1">{validationErrors.contactEmail}</div>}
              </RBForm.Group>
            </>
          )}

          <Actions>
            <SecondaryButton type="button" onClick={onHide} disabled={submitting || searching}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={submitting || searching || !searched || (isNew && hasErrors)}
            >
              {submitting ? (
                <>Processing...</>
              ) : foundProvider ? (
                'Add to My Panel'
              ) : (
                'Register & Add Provider'
              )}
            </PrimaryButton>
          </Actions>
        </form>
      )}
    </>
  );
}